/**
 * SmartDateInput — reusable date input with typing support and quick-select presets.
 *
 * Wraps Mantine's DateInput (which allows both keyboard typing and calendar picking)
 * and adds optional preset buttons (Today, Tomorrow, Next week, etc.) inside the
 * calendar dropdown so the portaled picker never covers preset chips or modal footers.
 *
 * Weekend days (Saturday & Sunday) are rendered in bold in the calendar dropdown
 * so they're visually distinct from weekdays.
 *
 * Typed US dates use strict parsing (no dayjs rollover or `new Date` fallback) — see
 * `usDateInputParsing.ts` and work items #1385, #1392, #1393, #1413.
 *
 * Mantine's `minDate` / `maxDate` on DateInput silently revert out-of-range typed
 * values on blur. We disable `fixOnBlur`, commit typed text ourselves, keep invalid
 * text visible with inline errors, and clear the committed `value` until the user fixes it.
 *
 * Usage:
 * ```tsx
 * <SmartDateInput
 *   label="Delivery Date"
 *   value={date}
 *   onChange={setDate}
 *   minDate={tomorrowDateString()}
 *   presets={[
 *     { value: tomorrowDateString(), label: 'Tomorrow' },
 *     { value: nextWeekDateString(), label: 'Next week' },
 *   ]}
 * />
 * ```
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { Box } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import { DateInput } from '@mantine/dates';
import type { DateInputProps } from '@mantine/dates';
import '@mantine/dates/styles.css';
import dayjs from 'dayjs';
import { DATE_INPUT_FORMAT, DATE_INPUT_PLACEHOLDER } from '../../utils/dateCalculations';
import {
  BIRTHDAY_MIN_YEAR,
  createStrictUsDateParser,
  formatYmdForUsDateInput,
  getUsDateInputValidationError,
  parseUsDateInputToYmd,
} from '../../utils/usDateInputParsing';
import classes from './SmartDateInput.module.css';
import { DATE_INPUT_CALENDAR_POPOVER_PROPS } from '../../utils/dateInputPopoverProps';

/** Keeps Mantine DateInput's internal inputValue aligned after we format on blur/commit. */
function applyFormattedInputValue(input: HTMLInputElement, formatted: string): void {
  if (input.value === formatted) {
    return;
  }
  const setNativeValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setNativeValue) {
    setNativeValue.call(input, formatted);
  } else {
    input.value = formatted;
  }
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * A quick-select preset shown as a compact button below the date input.
 */
export interface DatePreset {
  /** Date value in YYYY-MM-DD format */
  value: string;
  /** Button label (e.g. "Today", "Tomorrow") */
  label: string;
}

/**
 * Props for SmartDateInput.
 * Extends all Mantine DateInput props and adds optional `presets`.
 */
export interface SmartDateInputProps extends DateInputProps {
  /** Quick-select preset buttons rendered below the input */
  presets?: DatePreset[];
  /**
   * Earliest calendar year for typed US dates (birthday fields).
   * Wired into strict parsing and blur validation.
   */
  minYear?: number;
  /** Fired when the visible text in the input changes (typing, blur, presets). */
  onDisplayValueChange?: (display: string) => void;
  /**
   * Fired when inline typing validation blocks commit (invalid or out-of-range text).
   * Parent wizards use this to disable Next while `value` is cleared but error text remains (#1552).
   */
  onTypingValidationChange?: (hasBlockingTypingError: boolean) => void;
}

/**
 * Date input that supports both free-text typing and calendar selection,
 * plus optional quick-select preset buttons.
 */
export const SmartDateInput = forwardRef<HTMLInputElement, SmartDateInputProps>(
  (
    {
      presets,
      valueFormat,
      placeholder,
      getDayProps: externalGetDayProps,
      minDate,
      maxDate,
      minYear,
      error: externalError,
      onBlur: externalOnBlur,
      onChange,
      value,
      fixOnBlur: fixOnBlurProp,
      dateParser: externalDateParser,
      onDisplayValueChange,
      onTypingValidationChange,
      onKeyDown: externalOnKeyDown,
      popoverProps: externalPopoverProps,
      onClick: externalOnClick,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const enterKeyListenerCleanupRef = useRef<(() => void) | undefined>(undefined);
    const dateInputPresets = presets && presets.length > 0 ? presets : undefined;
    const resolvedFormat = valueFormat ?? DATE_INPUT_FORMAT;
    const usesUsFormat = resolvedFormat === DATE_INPUT_FORMAT;

    // Mantine spreads popoverProps after its internal `opened`, so this hides the calendar
    // after a failed commit (Enter or blur via commitTypedInputValue) while keeping focus
    // in the field (#1490). While suppressed, the calendar stays closed on refocus until
    // the user types or clicks the input again; the inline error remains visible.
    const [calendarSuppressed, setCalendarSuppressed] = useState(false);
    const externalErrorRef = useRef(externalError);
    externalErrorRef.current = externalError;
    const lastCommittedYmdRef = useRef<string | null>(null);

    const mergedPopoverProps = useMemo(
      () => ({
        ...DATE_INPUT_CALENDAR_POPOVER_PROPS,
        ...externalPopoverProps,
        ...(calendarSuppressed ? { opened: false } : {}),
      }),
      [externalPopoverProps, calendarSuppressed],
    );

    // Mantine fixOnBlur reverts invalid/out-of-range text; we commit on blur ourselves (#1413).
    const fixOnBlur = fixOnBlurProp ?? false;

    const parseOptions = useMemo(
      () => {
        const minYmd = typeof minDate === 'string' ? minDate : null;
        const maxYmd = typeof maxDate === 'string' ? maxDate : null;
        const resolvedMinYear =
          minYear ?? (typeof maxDate === 'string' && !minYmd ? BIRTHDAY_MIN_YEAR : undefined);

        return {
          minYmd,
          maxYmd,
          minYear: resolvedMinYear,
          boundsMessageStyle: minYmd ? ('scheduling' as const) : undefined,
        };
      },
      [minDate, maxDate, minYear],
    );

    const strictUsParser = useMemo(
      () => createStrictUsDateParser(parseOptions),
      [parseOptions],
    );

    const dateParser = usesUsFormat
      ? externalDateParser ?? strictUsParser
      : externalDateParser;

    const [typingError, setTypingError] = useState<string | null>(null);

    // Mantine DateInput clears the DOM when `value` is null; keep invalid typed text visible (#1413).
    const [invalidDisplay, setInvalidDisplay] = useState<string | null>(null);

    useEffect(() => {
      onTypingValidationChange?.(typingError != null);
    }, [typingError, onTypingValidationChange]);

    const mergedError = typingError ?? externalError;

    const syncDisplayValue = useCallback(() => {
      onDisplayValueChange?.(inputRef.current?.value ?? '');
    }, [onDisplayValueChange]);

    const normalizedValueYmd =
      typeof value === 'string' && value.trim() ? value.trim() : null;

    const noteCommittedYmd = useCallback((ymd: string) => {
      lastCommittedYmdRef.current = ymd;
      queueMicrotask(() => {
        if (lastCommittedYmdRef.current === ymd && externalErrorRef.current) {
          setCalendarSuppressed(true);
          lastCommittedYmdRef.current = null;
        }
      });
      window.setTimeout(() => {
        if (lastCommittedYmdRef.current === ymd && !externalErrorRef.current) {
          lastCommittedYmdRef.current = null;
        }
      }, 0);
    }, []);

    // Close the calendar only after a user commit when a parent form error appears (#1490).
    useLayoutEffect(() => {
      const pendingYmd = lastCommittedYmdRef.current;
      if (!pendingYmd || normalizedValueYmd !== pendingYmd) {
        return;
      }
      if (externalError && !typingError) {
        setCalendarSuppressed(true);
        lastCommittedYmdRef.current = null;
      }
    }, [externalError, normalizedValueYmd, typingError]);

    /**
     * Mantine DateInput clears its text when the controlled `value` becomes null.
     * While a bounds error is showing, keep a parsed YMD in the widget so the typed
     * date stays visible even though the parent `value` is null (#1413).
     */
    const mantineDateValue = useMemo((): string | null => {
      if (typingError && invalidDisplay && usesUsFormat) {
        const unboundYmd = parseUsDateInputToYmd(invalidDisplay, {
          minYear: parseOptions.minYear,
        });
        if (unboundYmd) {
          return unboundYmd;
        }
      }

      return normalizedValueYmd;
    }, [
      typingError,
      invalidDisplay,
      usesUsFormat,
      normalizedValueYmd,
      parseOptions.minYear,
    ]);

    useEffect(() => {
      if (!usesUsFormat || !mantineDateValue || !inputRef.current || typingError) {
        return;
      }

      const formatted = formatYmdForUsDateInput(mantineDateValue);
      applyFormattedInputValue(inputRef.current, formatted);
    }, [usesUsFormat, mantineDateValue, typingError]);

    /**
     * Merges weekend styling with any externally provided getDayProps.
     * Saturday (day 6) and Sunday (day 0) are rendered in bold.
     */
    const getDayProps = useCallback(
      (date: string) => {
        const dayOfWeek = dayjs(date).day();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const externalProps = externalGetDayProps?.(date) ?? {};

        if (isWeekend) {
          const disabled = (externalProps as { disabled?: boolean }).disabled;
          if (disabled) {
            return {
              ...externalProps,
              disabled: true,
            };
          }
          return {
            ...externalProps,
            style: {
              ...((externalProps as Record<string, unknown>).style as Record<string, unknown> | undefined),
              color: 'var(--mantine-color-black)',
              fontWeight: 700,
            },
          };
        }

        return externalProps;
      },
      [externalGetDayProps],
    );

    const handleChange = useCallback(
      (next: string | null) => {
        if (next) {
          noteCommittedYmd(next);
        }
        setTypingError(null);
        setInvalidDisplay(null);
        onChange?.(next);
        queueMicrotask(() => {
          if (next && usesUsFormat && inputRef.current) {
            const formatted = formatYmdForUsDateInput(next);
            if (inputRef.current.value !== formatted) {
              inputRef.current.value = formatted;
            }
          }
          syncDisplayValue();
        });
      },
      [noteCommittedYmd, onChange, syncDisplayValue, usesUsFormat],
    );

    const commitTypedInputValue = useCallback(
      (raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed) {
          const visible = inputRef.current?.value.trim() ?? '';
          if (visible) {
            syncDisplayValue();
            return;
          }
          setTypingError(null);
          setInvalidDisplay(null);
          onChange?.(null);
          syncDisplayValue();
          return;
        }

        if (!usesUsFormat) {
          syncDisplayValue();
          return;
        }

        const message = getUsDateInputValidationError(trimmed, parseOptions);
        setTypingError(message);
        if (message) {
          // Drop stale committed value so wizards block Next/Save (#1413).
          setInvalidDisplay(trimmed);
          onChange?.(null);
          syncDisplayValue();
          setCalendarSuppressed(true);
          return;
        }

        const ymd = parseUsDateInputToYmd(trimmed, parseOptions);
        if (!ymd) {
          onChange?.(null);
          syncDisplayValue();
          return;
        }

        const formatted = formatYmdForUsDateInput(ymd);
        if (inputRef.current) {
          applyFormattedInputValue(inputRef.current, formatted);
        }
        handleChange(ymd);
      },
      [handleChange, onChange, parseOptions, syncDisplayValue, usesUsFormat],
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        externalOnBlur?.(event);
        commitTypedInputValue(event.currentTarget.value);
      },
      [commitTypedInputValue, externalOnBlur],
    );

    /** Binds Enter-to-commit when Mantine attaches the real <input> (ref is often null on first paint). */
    const bindEnterCommitOnInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        enterKeyListenerCleanupRef.current?.();
        enterKeyListenerCleanupRef.current = undefined;

        if (!node) {
          return;
        }

        const onNativeKeyDown = (event: globalThis.KeyboardEvent) => {
          if (event.key !== 'Enter' || event.defaultPrevented) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          commitTypedInputValue(node.value);
        };

        node.addEventListener('keydown', onNativeKeyDown, true);
        enterKeyListenerCleanupRef.current = () => {
          node.removeEventListener('keydown', onNativeKeyDown, true);
        };
      },
      [commitTypedInputValue],
    );

    const mergedRef = useMergedRef(ref, inputRef, bindEnterCommitOnInputRef);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' && calendarSuppressed) {
          setCalendarSuppressed(false);
        }

        // Defense in depth for WebKit / Playwright: the capture-phase native listener
        // on the real <input> can miss Enter when the ref binds late (#1413 mobile E2E).
        if (
          usesUsFormat
          && event.key === 'Enter'
          && !event.defaultPrevented
          && !event.nativeEvent.isComposing
        ) {
          event.preventDefault();
          event.stopPropagation();
          commitTypedInputValue(event.currentTarget.value);
        }
        externalOnKeyDown?.(event);
      },
      [calendarSuppressed, commitTypedInputValue, externalOnKeyDown, usesUsFormat],
    );

    /**
     * Capture-phase Enter on the field shell (#1432). The document listener below commits
     * only when the date input is the event target; kept as defense-in-depth for wizard Enter guards.
     */
    const handleKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' || event.defaultPrevented) {
          return;
        }

        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-dates-input')) {
          return;
        }

        if (target !== inputRef.current) {
          return;
        }

        event.preventDefault();
        commitTypedInputValue(target.value);
      },
      [commitTypedInputValue],
    );

    useEffect(
      () => () => {
        enterKeyListenerCleanupRef.current?.();
        enterKeyListenerCleanupRef.current = undefined;
      },
      [],
    );

    /**
     * Document capture Enter commit when the date input is still the event target
     * (#1432 — wizard Enter guards and Mantine layering).
     */
    useEffect(() => {
      if (!usesUsFormat) {
        return undefined;
      }

      const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key !== 'Enter' || event.defaultPrevented) {
          return;
        }

        const input = inputRef.current;
        if (!input || event.target !== input) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        commitTypedInputValue(input.value);
      };

      document.addEventListener('keydown', onDocumentKeyDown, true);
      return () => {
        document.removeEventListener('keydown', onDocumentKeyDown, true);
      };
    }, [commitTypedInputValue, usesUsFormat]);

    const handleInput = useCallback(() => {
      setCalendarSuppressed(false);
      setTypingError(null);
      setInvalidDisplay(null);
      syncDisplayValue();
    }, [syncDisplayValue]);

    return (
      <Box className={classes.root} onKeyDownCapture={handleKeyDownCapture}>
        <DateInput
          {...rest}
          ref={mergedRef}
          valueFormat={resolvedFormat}
          placeholder={placeholder ?? DATE_INPUT_PLACEHOLDER}
          getDayProps={getDayProps}
          classNames={{ day: classes.day }}
          minDate={minDate}
          maxDate={maxDate}
          dateParser={dateParser}
          fixOnBlur={fixOnBlur}
          error={mergedError}
          value={mantineDateValue}
          popoverProps={mergedPopoverProps}
          presets={dateInputPresets}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onClick={(event) => {
            setCalendarSuppressed(false);
            externalOnClick?.(event);
          }}
        />
      </Box>
    );
  },
);

SmartDateInput.displayName = 'SmartDateInput';
